import { Directive, EventEmitter, Injectable, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Feature, FeatureCollection } from 'geojson';
import * as mapboxgl from 'mapbox-gl';
import * as moment from 'moment-timezone';
import * as _ from 'underscore';
import { FieldEventDTO } from '../../field-events/models/field-event';
import { FieldEventType } from '../../field-events/models/field-event-type';

@Directive()
@Injectable({
  providedIn: 'root'
})
export class FieldEventMarkerService {

  private fieldEventMarkers = [];
  private fieldEventOnScreen = [];

  public fieldEventClick: boolean;
  private allFieldEvents: any;
  private hoverPopup: mapboxgl.Popup = null;


  @Output() pointClicked: EventEmitter<FieldEventDTO> = new EventEmitter<FieldEventDTO>();

  private map: any;
  private fieldEventClusterMarkers: mapboxgl.Marker[] = [];
  private fieldEventClusterMarkersOnScreen: mapboxgl.Marker[] = [];

  // Spiderfy state: when the user clicks a cluster, fan its members out in a
  // circle so they're individually clickable without zooming.
  private spiderfiedClusterId: number = null;
  private spiderfiedMarkers: mapboxgl.Marker[] = [];
  private readonly spiderfyLinesSourceId = 'fieldEventSpiderfyLines';
  private readonly spiderfyLinesLayerId = 'fieldEventSpiderfyLines-layer';
  private spiderfyHandlersBound = false;
  private renderCallback: () => void;
  constructor(
    private popup: MatDialog,
  ) { }

  public getAllMarkersOnScreen() {
    return [...this.fieldEventOnScreen, ...this.fieldEventClusterMarkersOnScreen];
  }

  public async initializeMarkers(map, fieldEvents: FieldEventDTO[], //filterResults: FilterResults

  ) {
    this.map = map;
    this.allFieldEvents = fieldEvents;

    this.unspiderfy();

    for (let clusterMarker of this.fieldEventClusterMarkersOnScreen) {
      if (clusterMarker)
        clusterMarker.remove();
    }

    for (let marker of this.fieldEventOnScreen) {
      if (marker)
        marker.remove();
    }


    this.fieldEventClusterMarkers = [];
    this.fieldEventClusterMarkersOnScreen = [];
    this.fieldEventMarkers = [];
    this.fieldEventOnScreen = [];


    if (this.map.getLayer('fieldEventPointLayer'))
      this.map.removeLayer('fieldEventPointLayer');

    if (this.map.getSource("fieldEventSource"))
      this.map.removeSource("fieldEventSource");

    let eventFeatureCollection = <FeatureCollection>{
      type: 'FeatureCollection',
      features: []
    };


    let filteredFieldEvents = fieldEvents.filter(x => x.longitude &&
      x.latitude &&
      +x.latitude != 0 &&
      +x.longitude != 0 //&&
      //moment(x.createdDate).year() == +filterResults.selectedCropYears[0].itemName
    );


    if (filteredFieldEvents.length > 0) {

      filteredFieldEvents.forEach(fieldEvent => {
        let feature = <Feature>{
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [+fieldEvent.longitude, +fieldEvent.latitude]
          },
          properties: {
            ...fieldEvent,
            fieldEventTypeId: fieldEvent.fieldEventType.id,
            icon: fieldEvent.fieldEventType.fontAwesomeIconName,
            'marker-color': fieldEvent.fieldEventType.iconColor,
          }
        };

        eventFeatureCollection.features.push(feature);
      });
    }

    let uniqueTypes = _.uniq(filteredFieldEvents.map(x => x.fieldEventType.id));


    let source: mapboxgl.AnySourceData = {
      type: 'geojson',
      data: eventFeatureCollection,
      cluster: true,
      clusterRadius: 20,
      clusterProperties: uniqueTypes.reduce((current, type) => {
        current[type] = ['+', ['case', ['==', ['get', 'fieldEventTypeId'], type], 1, 0]];
        return current;
      }, {})
    };

    // Wait for the style to be fully loaded before interacting with the map
    // this.map.on('style.load', () => {
    this.map.addSource('fieldEventSource', source);

    // after the GeoJSON data is loaded, update markers on the screen on every frame
    if (this.renderCallback) this.map.off('render', this.renderCallback);
    this.renderCallback = () => {
      if (!this.map || !this.map.getSource('fieldEventSource') || !this.map.isSourceLoaded('fieldEventSource')) return;
      this.updateFieldEventMarkers();
      this.setFieldEventMarkers();
    };
    this.map.on('render', this.renderCallback);
    // });
  }


  private async setFieldEventMarkers() {

    if (this.map.getSource("fieldEventSource")) {


      // circle and symbol layers for rendering individual events (unclustered points)
      if (!this.map.getLayer('fieldEventPointLayer'))
        this.map.addLayer({
          'id': 'fieldEventPointLayer',
          //'type': 'circle',
          'source': 'fieldEventSource',
          'type': 'symbol'
        });


      const newMarkers = [];
      const features = this.map.querySourceFeatures('fieldEventSource');

      // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
      // and add it to the map if it's not there already
      for (const feature of features) {

        const coords = feature.geometry['coordinates'];
        const props = feature.properties;

        if (!Object.prototype.hasOwnProperty.call(props, 'cluster') || !props.cluster) {


          const id = props.id;

          let marker = this.fieldEventMarkers[id];
          if (!marker) {

            //let html = `<svg  width="${20}" height="${20} x="0px" y="-20px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 288 512"><path fill="${props['marker-color']}" d="M112 316.94v156.69l22.02 33.02c4.75 7.12 15.22 7.12 19.97 0L176 473.63V316.94c-10.39 1.92-21.06 3.06-32 3.06s-21.61-1.14-32-3.06zM144 0C64.47 0 0 64.47 0 144s64.47 144 144 144 144-64.47 144-144S223.53 0 144 0zm0 76c-37.5 0-68 30.5-68 68 0 6.62-5.38 12-12 12s-12-5.38-12-12c0-50.73 41.28-92 92-92 6.62 0 12 5.38 12 12s-5.38 12-12 12z"></path></svg>`;
            let html = `<img style="cursor: pointer;" src="https://cdn.mapmarker.io/api/v1/font-awesome/v5/pin?icon=${props['icon']}&background=${props['marker-color'].replace('#', '')}&color=FFF&size=30&hoffset=0&voffset=-1">`;
            const el = document.createElement('div');
            el.innerHTML = html;

            marker = this.fieldEventMarkers[id] = new mapboxgl.Marker(el, {}).setLngLat(coords);

            this.attachHoverTooltip(el, props, coords as [number, number]);

            marker.getElement().addEventListener('click', () => {
              this.fieldEventClick = true;


              let fieldEvent = this.allFieldEvents.find(x => x.id == props['id']);
              this.pointClicked.emit(fieldEvent);




            });
            // marker = fieldEventMarkers[id] = new mapboxgl.Marker({
            //   color: feature.properties['marker-color']
            // }).setLngLat(coords);
          }

          newMarkers[id] = marker;

          if (!this.fieldEventOnScreen[id]) marker.addTo(this.map);
        }

      }
      // for every marker we've added previously, remove those that are no longer visible
      for (const id in this.fieldEventOnScreen) {
        if (!newMarkers[id]) this.fieldEventOnScreen[id].remove();
      }


      this.fieldEventOnScreen = newMarkers;


    }
  }

  private updateFieldEventMarkers() {
    const newMarkers = [];
    const features = this.map.querySourceFeatures('fieldEventSource');

    // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
    // and add it to the map if it's not there already
    for (const feature of features) {

      const coords = feature.geometry['coordinates'];
      const props = feature.properties;

      if (props.cluster) {

        const id = props.cluster_id;

        let marker = this.fieldEventClusterMarkers[id];
        if (!marker) {
          const el = this.createFieldEventsDonutChart(props);
          this.attachSpiderfyClickHandler(el, id, coords);

          marker = this.fieldEventClusterMarkers[id] = new mapboxgl.Marker(el, {}).setLngLat(coords);
        }
        newMarkers[id] = marker;

        if (!this.fieldEventClusterMarkersOnScreen[id]) marker.addTo(this.map);
      }

    }
    // for every marker we've added previously, remove those that are no longer visible
    for (const id in this.fieldEventClusterMarkersOnScreen) {
      if (!newMarkers[id]) this.fieldEventClusterMarkersOnScreen[id].remove();
    }


    this.fieldEventClusterMarkersOnScreen = newMarkers;
  }

  private createFieldEventsDonutChart(props): HTMLElement {
    const offsets = [];
    let filteredEventTypes: FieldEventType[] = [];

    const counts = [];


    let fieldEventTypes: FieldEventType[] = _.uniq(this.allFieldEvents.map(x => x.fieldEventType), true, x => x.id);

    for (let key in props) {
      let includedFieldEventType = fieldEventTypes.find(x => x.id.toString() == key)
      if (includedFieldEventType) {
        filteredEventTypes.push(includedFieldEventType)
        counts.push(props[key]);
      }
    }

    let total = 0;

    for (const count of counts) {
      offsets.push(total);
      total += count;
    }
    const fontSize =
      total >= 1000 ? 22 : total >= 100 ? 20 : total >= 10 ? 18 : 16;
    const r =
      total >= 1000 ? 50 : total >= 100 ? 32 : total >= 10 ? 24 : 18;
    const r0 = Math.round(r * 0.6);
    const w = r * 2;

    let html = `<div>
  <svg width="${w}" height="${w}" viewbox="0 0 ${w} ${w}" text-anchor="middle" style="font: ${fontSize}px sans-serif; display: block">`;

    for (let i = 0; i < counts.length; i++) {
      html += this.donutSegment(
        offsets[i] / total,
        (offsets[i] + counts[i]) / total,
        r,
        r0,
        filteredEventTypes[i].iconColor
      );
    }
    html += `<circle cx="${r}" cy="${r}" r="${r0}" fill="white" />
  <text dominant-baseline="central" transform="translate(${r}, ${r})">
  ${total.toLocaleString()}
  </text>
  </svg>
  </div>`;

    const el = document.createElement('div');

    el.innerHTML = html;

    return el;
  }

  private attachHoverTooltip(el: HTMLElement, props: any, coords: [number, number]) {
    el.addEventListener('mouseenter', () => {
      if (!this.map) return;
      const fieldEvent = (this.allFieldEvents || []).find((x: any) => x.id == props['id']);
      const createdBy = (fieldEvent && (fieldEvent.createdByName || fieldEvent.createdBy))
        || props['createdByName'] || props['createdBy'] || 'Unknown';
      const createdRaw = (fieldEvent && fieldEvent.createdDate) || props['createdDate'];
      const createdDate = createdRaw ? moment(createdRaw).format('MMM D, YYYY h:mm A') : '';
      const eventTypeName = (fieldEvent && fieldEvent.fieldEventType && fieldEvent.fieldEventType.name) || '';

      const html = `<div style="font-size:12px; line-height:1.3; color:#000;">
          ${eventTypeName ? `<div><strong>${this.escapeHtml(eventTypeName)}</strong></div>` : ''}
          <div>${this.escapeHtml(createdBy)}</div>
          <div>${this.escapeHtml(createdDate)}</div>
        </div>`;

      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
      this.hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 18
      })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(this.map);
    });

    el.addEventListener('mouseleave', () => {
      if (this.hoverPopup) {
        this.hoverPopup.remove();
        this.hoverPopup = null;
      }
    });
  }

  private buildFieldEventLabel(fieldEvent: any, props: any): string {
    try {
      const parts: string[] = [];
      const typeName = fieldEvent && fieldEvent.fieldEventType && fieldEvent.fieldEventType.name;
      const sampleId = (fieldEvent && fieldEvent.sampleId) || (props && props['sampleId']);
      const blockName = fieldEvent && fieldEvent.block && fieldEvent.block.name;
      if (sampleId) parts.push(String(sampleId));
      else if (typeName) parts.push(String(typeName));
      if (blockName && !sampleId) parts.push(String(blockName));
      return parts.length ? parts.join(' – ') : 'Event';
    } catch (e) {
      return 'Event';
    }
  }

  private escapeHtml(value: string): string {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private attachSpiderfyClickHandler(el: HTMLElement, clusterId: number, centerCoords: [number, number]) {
    el.style.cursor = 'pointer';
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.spiderfiedClusterId === clusterId) {
        this.unspiderfy();
      } else {
        this.spiderfyCluster(clusterId, centerCoords);
      }
    });
  }

  private spiderfyCluster(clusterId: number, centerCoords: [number, number]) {
    const source: any = this.map && this.map.getSource('fieldEventSource');
    if (!source || typeof source.getClusterLeaves !== 'function') return;

    this.unspiderfy();

    source.getClusterLeaves(clusterId, Number.MAX_SAFE_INTEGER, 0, (err, leaves) => {
      if (err || !leaves || !leaves.length) return;

      this.spiderfiedClusterId = clusterId;
      const centerPx = this.map.project(centerCoords);
      const count = leaves.length;
      // Single ring up to ~10, then a second ring for larger counts.
      const legLength = count <= 10 ? 42 : 60;
      const lineFeatures: any[] = [];

      leaves.forEach((leaf: any, i: number) => {
        const angle = (2 * Math.PI * i) / count - Math.PI / 2;
        const px = {
          x: centerPx.x + legLength * Math.cos(angle),
          y: centerPx.y + legLength * Math.sin(angle)
        };
        const lngLat = this.map.unproject(px);
        const coords: [number, number] = [lngLat.lng, lngLat.lat];
        const props = leaf.properties || {};
        const markerColor = (props['marker-color'] || '#3887be').replace('#', '');
        const icon = props['icon'] || 'map-marker';

        const allEvents: any[] = this.allFieldEvents || [];
        const fullEvent: any = allEvents.find((x: any) => x.id == props['id']);
        const labelText = this.buildFieldEventLabel(fullEvent, props);

        const el = document.createElement('div');
        el.style.display = 'flex';
        el.style.flexDirection = 'column';
        el.style.alignItems = 'center';
        el.innerHTML = `
          <img style="cursor: pointer;" src="https://cdn.mapmarker.io/api/v1/font-awesome/v5/pin?icon=${icon}&background=${markerColor}&color=FFF&size=30&hoffset=0&voffset=-1">
          <div style="margin-top: 2px; padding: 1px 4px; background: rgba(255,255,255,0.9); border-radius: 3px; font-size: 11px; font-weight: 600; color: #000; white-space: nowrap; pointer-events: none; text-shadow: 0 0 2px #fff;">${this.escapeHtml(labelText)}</div>
        `;

        this.attachHoverTooltip(el, props, coords);

        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.fieldEventClick = true;
          const fieldEvent = this.allFieldEvents.find((x: any) => x.id == props['id']);
          this.pointClicked.emit(fieldEvent);
          this.unspiderfy();
        });

        const marker = new mapboxgl.Marker(el).setLngLat(coords).addTo(this.map);
        this.spiderfiedMarkers.push(marker);

        lineFeatures.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [centerCoords, coords] },
          properties: {}
        });
      });

      const lineData: any = { type: 'FeatureCollection', features: lineFeatures };
      const existing: any = this.map.getSource(this.spiderfyLinesSourceId);
      if (existing) {
        existing.setData(lineData);
      } else {
        this.map.addSource(this.spiderfyLinesSourceId, { type: 'geojson', data: lineData });
        this.map.addLayer({
          id: this.spiderfyLinesLayerId,
          type: 'line',
          source: this.spiderfyLinesSourceId,
          paint: { 'line-color': '#888', 'line-width': 1, 'line-opacity': 0.7 }
        });
      }

      this.bindSpiderfyMapHandlers();
    });
  }

  public unspiderfy() {
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
    if (this.spiderfiedMarkers.length) {
      this.spiderfiedMarkers.forEach(m => m.remove());
    }
    this.spiderfiedMarkers = [];
    this.spiderfiedClusterId = null;
    if (this.map) {
      if (this.map.getLayer(this.spiderfyLinesLayerId)) {
        this.map.removeLayer(this.spiderfyLinesLayerId);
      }
      if (this.map.getSource(this.spiderfyLinesSourceId)) {
        this.map.removeSource(this.spiderfyLinesSourceId);
      }
    }
  }

  private bindSpiderfyMapHandlers() {
    if (this.spiderfyHandlersBound || !this.map) return;
    this.spiderfyHandlersBound = true;

    this.map.on('zoomstart', () => this.unspiderfy());

    this.map.on('click', (e: any) => {
      if (!this.spiderfiedMarkers.length) return;
      const target = e && e.originalEvent && e.originalEvent.target;
      const clickedSpiderfiedMarker = target && this.spiderfiedMarkers.some(m => {
        const el = m.getElement();
        return el === target || el.contains(target);
      });
      const clickedClusterMarker = target && Object.keys(this.fieldEventClusterMarkers).some(k => {
        const marker = this.fieldEventClusterMarkers[k];
        if (!marker) return false;
        const el = marker.getElement();
        return el === target || el.contains(target);
      });
      if (!clickedSpiderfiedMarker && !clickedClusterMarker) {
        this.unspiderfy();
      }
    });
  }

  private donutSegment(start, end, r, r0, color) {
    if (end - start === 1) end -= 0.00001;
    const a0 = 2 * Math.PI * (start - 0.25);
    const a1 = 2 * Math.PI * (end - 0.25);
    const x0 = Math.cos(a0),
      y0 = Math.sin(a0);
    const x1 = Math.cos(a1),
      y1 = Math.sin(a1);
    const largeArc = end - start > 0.5 ? 1 : 0;

    // draw an SVG path
    return `<path d="M ${r + r0 * x0} ${r + r0 * y0} L ${r + r * x0} ${r + r * y0
      } A ${r} ${r} 0 ${largeArc} 1 ${r + r * x1} ${r + r * y1} L ${r + r0 * x1
      } ${r + r0 * y1} A ${r0} ${r0} 0 ${largeArc} 0 ${r + r0 * x0} ${r + r0 * y0
      }" fill="${color}" />`;
  }


}
